import { jsPDF } from "jspdf";
import type { Plan, Marker } from "../types/domain.types";
import { getTileUrlRequest, getTileUrlsBatchRequest } from "../api/plansApi";
import { MARKER_CATEGORY_ICON } from "./markerCategory";

const TILE_SIZE = 256;
// Limites prácticos del canvas en navegadores. Si se sobrepasa, hacemos
// downscale al zoom inmediatamente inferior.
const MAX_CANVAS_DIM = 16384;

interface ExportProgress {
  loaded: number;
  total: number;
}

/**
 * Carga una imagen desde URL en HTMLImageElement.
 * En tiles firmados de R2, crossOrigin no es necesario (mismo flujo que TileLayer),
 * pero forzamos anonymous para que el canvas no quede tainted.
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}

/**
 * Determina el zoom mas alto cuyo canvas (tileWidth/tileHeight escalados a ese zoom)
 * cabe dentro de MAX_CANVAS_DIM.
 */
function pickExportZoom(plan: Plan): number {
  for (let z = plan.tileMaxZoom; z >= plan.tileMinZoom; z--) {
    const factor = 1 / Math.pow(2, plan.tileMaxZoom - z);
    const w = plan.tileWidth * factor;
    const h = plan.tileHeight * factor;
    if (w <= MAX_CANVAS_DIM && h <= MAX_CANVAS_DIM) return z;
  }
  return plan.tileMinZoom;
}

/**
 * Compone el plano completo (maxima resolucion segura) con sus marcadores
 * en un HTMLCanvasElement. El canvas preserva la proporcion del plano
 * (tileWidth x tileHeight escalados uniformemente).
 */
export type MarkerLabelField = "qrCode" | "comment";

export async function renderPlanCanvas(
  plan: Plan,
  markers: Marker[],
  onProgress?: (p: ExportProgress) => void,
  labelField: MarkerLabelField = "qrCode",
): Promise<HTMLCanvasElement> {
  const zoom = pickExportZoom(plan);
  const factor = 1 / Math.pow(2, plan.tileMaxZoom - zoom);
  const canvasWidth = Math.ceil(plan.tileWidth * factor);
  const canvasHeight = Math.ceil(plan.tileHeight * factor);

  const cols = Math.ceil(canvasWidth / TILE_SIZE);
  const rows = Math.ceil(canvasHeight / TILE_SIZE);
  const totalTiles = cols * rows;

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo crear contexto 2D");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // 1) Generar lista completa de coordenadas
  const coords: Array<{ x: number; y: number }> = [];
  for (let x = 0; x < cols; x++) {
    for (let y = 0; y < rows; y++) {
      coords.push({ x, y });
    }
  }

  // 2) Descargar tiles en chunks para evitar que las URLs firmadas expiren
  // (TTL backend = 15 min). Cada chunk obtiene URLs frescas vía batch.
  let loaded = 0;
  const concurrency = 8;
  const CHUNK_SIZE = 1000;

  for (let i = 0; i < coords.length; i += CHUNK_SIZE) {
    const chunk = coords.slice(i, i + CHUNK_SIZE);

    const signedUrlMap = new Map<string, string>();
    try {
      const { urls } = await getTileUrlsBatchRequest(plan.id, zoom, chunk);
      for (const item of urls) {
        signedUrlMap.set(`${item.x}/${item.y}`, item.signedUrl);
      }
    } catch (err) {
      console.error(
        `Batch de URLs falló para chunk ${i}-${i + chunk.length}, se reintentarán individualmente:`,
        err,
      );
    }

    const queue = [...chunk];

    async function worker() {
      while (queue.length > 0) {
        const job = queue.shift();
        if (!job) return;
        try {
          const cacheKey = `${job.x}/${job.y}`;
          let signedUrl = signedUrlMap.get(cacheKey);

          if (!signedUrl) {
            const res = await getTileUrlRequest(plan.id, zoom, job.x, job.y);
            signedUrl = res.signedUrl;
          }

          const img = await loadImage(signedUrl);
          ctx!.drawImage(img, job.x * TILE_SIZE, job.y * TILE_SIZE);
        } catch (err) {
          console.warn(`Tile ${job.x}/${job.y} no se pudo cargar, se deja en blanco:`, err);
        } finally {
          loaded++;
          onProgress?.({ loaded, total: totalTiles });
        }
      }
    }

    await Promise.all(
      Array.from({ length: Math.min(concurrency, queue.length) }, () => worker()),
    );
  }

  // Pre-cargar iconos de categoria una sola vez
  const iconCache = new Map<string, HTMLImageElement>();
  const uniqueIconUrls = new Set<string>();
  for (const m of markers) {
    if (m.category) uniqueIconUrls.add(MARKER_CATEGORY_ICON[m.category]);
  }
  await Promise.all(
    Array.from(uniqueIconUrls).map(async (url) => {
      try {
        const img = await loadImage(url);
        iconCache.set(url, img);
      } catch {
        // ignore
      }
    }),
  );

  // Tamaño "largo" del icono en pixels del canvas. Legible a maxima resolucion.
  // El lado corto se calcula a partir del aspect ratio natural del PNG para
  // NO deformar la marca.
  const iconLongSideBase = 64; // px a zoom maximo
  const iconLongSide = Math.max(24, Math.round(iconLongSideBase * factor));

  // Paso 1: dibujar iconos y registrar bbox del icono
  interface PlacedMarker {
    m: Marker;
    cx: number;
    cy: number;
    w: number;
    h: number;
  }
  const placed: PlacedMarker[] = [];

  for (const m of markers) {
    const cx = m.coordX * factor;
    const cy = m.coordY * factor;
    let w = iconLongSide;
    let h = iconLongSide;
    if (m.category) {
      const url = MARKER_CATEGORY_ICON[m.category];
      const img = iconCache.get(url);
      if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
        const ratio = img.naturalWidth / img.naturalHeight;
        if (ratio >= 1) {
          w = iconLongSide;
          h = iconLongSide / ratio;
        } else {
          h = iconLongSide;
          w = iconLongSide * ratio;
        }
        ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
        placed.push({ m, cx, cy, w, h });
        continue;
      }
    }
    // Fallback: punto azul
    const r = Math.max(6, iconLongSide / 6);
    ctx.beginPath();
    ctx.fillStyle = "#3b82f6";
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.stroke();
    placed.push({ m, cx, cy, w: r * 2, h: r * 2 });
  }

  // Paso 2: dibujar etiquetas (qrCode) con deteccion de colision simple.
  // Intenta primero "abajo" del icono, luego "arriba", luego "derecha",
  // luego "izquierda". Si todas colisionan, omite la etiqueta.
  const fontPx = Math.max(10, Math.round(14 * factor));
  ctx.font = `600 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;
  ctx.textBaseline = "top";
  const padX = 4;
  const padY = 2;
  const gap = 4; // separacion icono-etiqueta

  const labelBoxes: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
  const overlaps = (
    a: { x1: number; y1: number; x2: number; y2: number },
    b: { x1: number; y1: number; x2: number; y2: number },
  ) => !(a.x2 < b.x1 || b.x2 < a.x1 || a.y2 < b.y1 || b.y2 < a.y1);

  for (const p of placed) {
    const raw =
      labelField === "comment" ? (p.m.comment ?? "") : p.m.qrCode;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const text = trimmed.length > 24 ? `${trimmed.slice(0, 22)}…` : trimmed;
    const metrics = ctx.measureText(text);
    const tw = metrics.width;
    const th = fontPx;
    const boxW = tw + padX * 2;
    const boxH = th + padY * 2;

    const candidates = [
      // abajo
      { bx: p.cx - boxW / 2, by: p.cy + p.h / 2 + gap },
      // arriba
      { bx: p.cx - boxW / 2, by: p.cy - p.h / 2 - gap - boxH },
      // derecha
      { bx: p.cx + p.w / 2 + gap, by: p.cy - boxH / 2 },
      // izquierda
      { bx: p.cx - p.w / 2 - gap - boxW, by: p.cy - boxH / 2 },
    ];

    let chosen: { bx: number; by: number } | null = null;
    for (const c of candidates) {
      const box = {
        x1: c.bx,
        y1: c.by,
        x2: c.bx + boxW,
        y2: c.by + boxH,
      };
      if (!labelBoxes.some((lb) => overlaps(lb, box))) {
        chosen = c;
        labelBoxes.push(box);
        break;
      }
    }
    if (!chosen) continue; // muy aglomerado: omitir etiqueta

    // Fondo blanco semiopaco
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.fillRect(chosen.bx, chosen.by, boxW, boxH);
    ctx.strokeStyle = "rgba(0,0,0,0.25)";
    ctx.lineWidth = 1;
    ctx.strokeRect(chosen.bx, chosen.by, boxW, boxH);
    // Texto
    ctx.fillStyle = "#0f172a";
    ctx.fillText(text, chosen.bx + padX, chosen.by + padY);
  }

  return canvas;
}

/**
 * Exporta el plano a PNG.
 */
export async function exportPlanPng(
  plan: Plan,
  markers: Marker[],
  onProgress?: (p: ExportProgress) => void,
  labelField: MarkerLabelField = "qrCode",
): Promise<Blob> {
  const canvas = await renderPlanCanvas(plan, markers, onProgress, labelField);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("No se pudo generar el PNG"));
      },
      "image/png",
      1,
    );
  });
}

/**
 * Exporta el plano a PDF de una sola pagina. El tamaño de la pagina
 * se deriva del canvas (a 96 DPI nominal) para preservar exactamente la
 * proporcion horizontal/vertical del plano y no deformar los marcadores.
 */
export async function exportPlanPdf(
  plan: Plan,
  markers: Marker[],
  onProgress?: (p: ExportProgress) => void,
  labelField: MarkerLabelField = "qrCode",
): Promise<Blob> {
  const canvas = await renderPlanCanvas(plan, markers, onProgress, labelField);
  const dataUrl = canvas.toDataURL("image/jpeg", 0.92);

  // Usamos unidades en puntos (pt). 1 px @ 96dpi = 0.75 pt.
  const pageWidthPt = (canvas.width * 72) / 96;
  const pageHeightPt = (canvas.height * 72) / 96;
  const orientation = pageWidthPt >= pageHeightPt ? "landscape" : "portrait";

  const pdf = new jsPDF({
    orientation,
    unit: "pt",
    format: [pageWidthPt, pageHeightPt],
    compress: true,
  });
  pdf.addImage(dataUrl, "JPEG", 0, 0, pageWidthPt, pageHeightPt, undefined, "FAST");
  return pdf.output("blob");
}

// Compat: alias previo usado por ViewerPage
export const exportPlanImage = exportPlanPdf;

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
